import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EXPENSE_MEDIA_ENTITY_TYPE,
  PAYMENT_METHODS,
  paiseToRupees,
  parseRupees,
  shopDateInputValue,
  type Expense,
  type ExpenseCategory,
  type PaymentMethod,
} from '@tailoring-crm/shared';
import { MediaUploader } from '../media/media-uploader.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { useRecordExpense, useUpdateExpense } from '../../lib/expenses/use-expenses.js';
import { formatCurrency } from '../../lib/format.js';

/**
 * Recording what the shop spent.
 *
 * Two dates, and only one of them is usually visible. A shop paying cash across the counter
 * fills in one date and the form quietly uses it for both; the "paid on a different day" tick is
 * for the case that actually needs it — a fabric bill taken on account and settled next month —
 * and it is the only place in the product where the cash/accrual distinction is something a user
 * TYPES rather than reads. Hiding it behind a tick keeps the common case to four fields.
 *
 * The bill photograph goes through the shared uploader, the same component orders and
 * measurement profiles mount. It appears only when editing an expense that already exists,
 * because media attaches to a saved parent — a photograph taken during creation would have
 * nothing to hang off.
 */

export interface RecordExpenseDialogProps {
  categories: readonly ExpenseCategory[];
  /** Set when editing. Absent when recording a new one. */
  expense?: Expense | null;
  onClose: () => void;
  onSaved?: (expense: Expense) => void;
}

/** `paiseToRupees` for a text input: no symbol, no grouping, no trailing `.00`. */
function rupeeInputValue(paise: number): string {
  if (paise <= 0) return '';
  const rupees = paiseToRupees(paise);
  return Number.isInteger(rupees) ? String(rupees) : rupees.toFixed(2);
}

export function RecordExpenseDialog({
  categories,
  expense = null,
  onClose,
  onSaved,
}: RecordExpenseDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const record = useRecordExpense();
  const update = useUpdateExpense();
  const editing = expense !== null;

  const today = shopDateInputValue(new Date());
  const [amount, setAmount] = useState(() => (expense ? rupeeInputValue(expense.amountPaise) : ''));
  const [categoryId, setCategoryId] = useState(
    () => expense?.categoryId ?? categories[0]?.id ?? '',
  );
  const [incurredOn, setIncurredOn] = useState(() => expense?.incurredOn.slice(0, 10) ?? today);
  const [separatePaidOn, setSeparatePaidOn] = useState(
    () => expense !== null && expense.paidOn !== null && expense.paidOn !== expense.incurredOn,
  );
  const [unpaid, setUnpaid] = useState(() => expense !== null && expense.paidOn === null);
  const [paidOn, setPaidOn] = useState(
    () => expense?.paidOn?.slice(0, 10) ?? expense?.incurredOn.slice(0, 10) ?? today,
  );
  const [vendor, setVendor] = useState(() => expense?.vendor ?? '');
  const [method, setMethod] = useState<PaymentMethod>(() => expense?.method ?? 'cash');
  const [note, setNote] = useState(() => expense?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  /** Rupees as typed, in paise. Unparsable counts as nothing rather than throwing mid-keystroke. */
  const amountPaise = ((): number => {
    if (amount.trim() === '') return 0;
    try {
      return parseRupees(amount);
    } catch {
      return 0;
    }
  })();

  const canSubmit = amountPaise > 0 && Number.isInteger(amountPaise) && categoryId !== '';

  const paidOnValue = (): string | null | undefined => {
    if (unpaid) return null;
    if (!separatePaidOn) return incurredOn;
    return paidOn;
  };

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      const saved = editing
        ? await update.mutateAsync({
            expenseId: expense.id,
            input: {
              amountPaise,
              categoryId,
              incurredOn,
              paidOn: paidOnValue(),
              vendor: vendor.trim() === '' ? null : vendor.trim(),
              method,
              note: note.trim() === '' ? null : note.trim(),
            },
          })
        : await record.mutateAsync({
            amountPaise,
            categoryId,
            incurredOn,
            paidOn: paidOnValue(),
            ...(vendor.trim() ? { vendor: vendor.trim() } : {}),
            method,
            ...(note.trim() ? { note: note.trim() } : {}),
          });

      toast({
        title: editing
          ? t('expenses.updated', { amount: formatCurrency(saved.amountPaise) })
          : t('expenses.recorded', { amount: formatCurrency(saved.amountPaise) }),
        variant: 'success',
      });
      onSaved?.(saved);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? t('expenses.editTitle') : t('expenses.recordTitle')}</DialogTitle>
          <DialogDescription>{t('expenses.recordHint')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="expense-amount">{t('expenses.amount')}</Label>
            <Input
              id="expense-amount"
              inputMode="decimal"
              autoFocus
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="expense-category">{t('expenses.category')}</Label>
            <select
              id="expense-category"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {categories.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expense-date">{t('expenses.incurredOn')}</Label>
              <Input
                id="expense-date"
                type="date"
                value={incurredOn}
                onChange={(event) => setIncurredOn(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="expense-method">{t('expenses.method')}</Label>
              <select
                id="expense-method"
                value={method}
                onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                {PAYMENT_METHODS.map((one) => (
                  <option key={one} value={one}>
                    {t(`payments.methods.${one}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* The cash/accrual distinction, in the one place a shop actually types it. */}
          <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5 size-4"
                checked={separatePaidOn || unpaid}
                onChange={(event) => {
                  setSeparatePaidOn(event.target.checked);
                  if (!event.target.checked) setUnpaid(false);
                }}
              />
              <span>{t('expenses.paidOnDifferentDay')}</span>
            </label>

            {separatePaidOn || unpaid ? (
              <div className="flex flex-col gap-2 pl-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={unpaid}
                    onChange={(event) => setUnpaid(event.target.checked)}
                  />
                  <span>{t('expenses.notPaidYet')}</span>
                </label>
                {!unpaid ? (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="expense-paid-on">{t('expenses.paidOn')}</Label>
                    <Input
                      id="expense-paid-on"
                      type="date"
                      value={paidOn}
                      className="max-w-56"
                      onChange={(event) => setPaidOn(event.target.value)}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="expense-vendor">{t('expenses.vendor')}</Label>
            <Input
              id="expense-vendor"
              value={vendor}
              placeholder={t('expenses.vendorPlaceholder')}
              onChange={(event) => setVendor(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="expense-note">{t('expenses.note')}</Label>
            <Textarea
              id="expense-note"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>

          {editing ? (
            <div className="flex flex-col gap-1.5">
              <Label>{t('expenses.bill')}</Label>
              <MediaUploader
                entityType={EXPENSE_MEDIA_ENTITY_TYPE}
                entityId={expense.id}
                kind="bill"
                maxFiles={5}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('expenses.billAfterSaving')}</p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || record.isPending || update.isPending}
            onClick={() => void submit()}
          >
            {editing ? t('common.save') : t('expenses.recordSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
